// EmployeeLeavePolicy

import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_LEAVE_POLICY } from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { CalendarClock, CheckCircle, Edit } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";

const EmployeeLeavePolicy = () => {

    const [EmployeeLeavePolicyData, setEmployeeLeavePolicyData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    let navigate = useNavigate()
    const { add: addPermission, edit: editPermission, draft: draftPermission } = checkUserPermissionFunc('master', 'Leave Type', 'SubMenu')

    const EmployeeLeavePolicyColumns = [
        {
            accessor: "code",
            Header: "Policy Code",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "leave_policy_option",
            Header: "Policy Information",
            sortable: true,
            filterable: true,
            width: "220px",
            Cell: ({ row }) => {
                const { request_type, leave_name } = row;

                if (!request_type && !leave_name)
                    return <span className="text-gray-400 italic">No Info</span>;

                const formatRequestType = (type) => {
                    return type ? type.replace(/_/g, " ") : "";
                };

                return (
                    <div className="flex flex-col gap-1.5">
                        {/* Leave Name */}
                        {leave_name && (
                            <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100 leading-snug">
                                <span className=" text-xs font-thin text-gray-500">Leave name :</span> {leave_name}
                            </div>
                        )}

                        {/* Request Type */}
                        {request_type && (
                            <div className="inline-flex items-center gap-1">
                                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                                <span className="text-[12px] text-gray-500 dark:text-gray-400 capitalize tracking-wide">
                                    {formatRequestType(request_type)}
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessor: "no_of_days",
            Header: "No Of Days/Hrs",
            Cell: ({ value, row }) => {
                const displayValue =
                    (["leave", "work_from_home"].includes(row.request_type)
                        ? row.no_of_days
                        : row.no_of_hours) || "-";

                return <p>{displayValue}</p>;
            }
        },
        {
            accessor: "leave_expiry",
            Header: "Validity Period",
            sortable: true,
            width: "120px",
            Cell: ({ value }) => {
                if (!value) return "-";

                const periodMap = {
                    1: "Monthly",
                    3: "Quarterly",
                    12: "Yearly",
                    6: "Half Yearly"
                };

                return (
                    <span className="text-gray-700 dark:text-gray-300">
                        {periodMap[value] || `${value} months`}
                    </span>
                );
            },
        },
        {
            accessor: "leave_carry_forward",
            Header: "Carry Forward",
            sortable: true,
            width: "120px",
            Cell: ({ value }) => (
                <span className={cn(
                    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                    value
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                )}>
                    {value ? (
                        <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Yes
                        </>
                    ) : (
                        "No"
                    )}
                </span>
            ),
        },
        {
            accessor: "is_active",
            Header: "Status",
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Action",
                sortable: false,
                Cell: ({ row }) => {
                    const handleOpenEdit = () => {
                        navigate('/master/leavetype/add', { state: { fromEdit: true, editDetails: row } });

                    };

                    return (
                        <div className="flex items-center gap-2">
                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenEdit}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        </div>
                    );
                },
            }
        ] : [])
    ];

    const handlePolicySetup = () => {
        navigate('/master/leavetype/add')
    }

    return (
        <ContainerCard>
            {/* Header */}
            <ModuleTitleCard Icon={CalendarClock} title={'Leave Type'} subtitle="Define different types of leave and time-off policies" totalRecords={totalRecords} buttonEnable={!!addPermission} setOpenDialog={handlePolicySetup} buttonName={'Add Leave type'} />

            <Reactable
                columns={EmployeeLeavePolicyColumns}
                data={EmployeeLeavePolicyData}
                setData={setEmployeeLeavePolicyData}
                setTotalCount={setTotalRecords}
                url={EMPLOYEE_LEAVE_POLICY}
                serverSide={true}
                tableHeight="calc(100vh - 320px)"
                rowSize={editPermission ? "sm" : null}
                isDraftRequired={!!draftPermission}
                isCSVRequired={true}
                isPDFRequired={true}
            />
        </ContainerCard >
    );
}

export default EmployeeLeavePolicy;